import React, { useMemo, useState,useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Stack, TextField, FormControl, InputLabel, Select, MenuItem,
    Paper, Typography, Divider, Chip
} from "@mui/material";

import { DiaryItemType, DiaryCategory, DiaryVisibility } from "../../models/diaryTypes";
import type { DiaryItemDto, DiaryItemUpsertDtoEx } from "../../models/diaryTypes";
import { DiaryItemStatus } from "../../models/diaryTypes";

import { useApiClient } from '../../components/useApiClient';
import { useUser } from '../../components/UserContext';
import useAuthToken from '../../functions/useAuthToken';
import type { DiaryPaymentDetailsDto } from "../../models/Payments";

import { SimpleDateInput, DateTimeRangeInput, DueDateInput } from '../../components/DateInput/DatePresets';

type DiaryChildModalProps = {
    open: boolean;
    parent: DiaryItemDto;
    child: DiaryItemDto | null; // null = create
    onClose: () => void;
    onSaved: (id: number) => Promise<DiaryItemDto>; // refresh parent
    updateDiaryItem: (id: number, dto: DiaryItemUpsertDtoEx) => void;
    createChildDiaryItem: (parentId: number, dto: DiaryItemUpsertDtoEx) => Promise<any>;
};

export default function DiaryChildModal({ open, parent, child, onClose, onSaved, updateDiaryItem, createChildDiaryItem }: DiaryChildModalProps) {
    const isEdit = !!child;
    const isViewOnly = child?.status !== DiaryItemStatus.Planned ?? false;
    const isPaymentItem = child?.category === DiaryCategory.Payment ?? false;

    //Handle client roles and Api
    const { activeClientId, isAdmin, isOwner } = useUser();
    const getAccessToken = useAuthToken();
    const { get } = useApiClient();

    const initial: DiaryItemUpsertDtoEx = useMemo(() => {
        if (child) {
            return {
                title: child.title,
                description: child.description ?? "",
                type: child.type,
                category: child.category,
                visibility: child.visibility,
                startUtc: child.startUtc ?? null,
                endUtc: child.endUtc ?? null,
                dueUtc: child.dueUtc ?? null,
                allDay: child.allDay,
                timezoneId: child.timezoneId ?? null,
                recurrenceRule: null,
                parentDiaryItemId: parent?.id ?? null,
                shares: child.shares ?? [],
                reminders: child.reminders ?? [],
                links: child.links ?? [],
                startUtcStr: child.startUtcStr,
                endUtcStr: child.endUtcStr,
                dueUtcStr: child.dueUtcStr,
            };
        }

        // Create defaults
        return {
            title: parent?.title ?? "",
            description: parent?.description ?? "",
            type: DiaryItemType.Task,
            category: parent?.category,
            visibility: parent?.visibility,
            startUtc: null,
            endUtc: null,
            dueUtc: new Date().toISOString(),
            allDay: false,
            timezoneId: parent?.timezoneId ?? null,
            recurrenceRule: null,
            parentDiaryItemId: parent?.id ?? null,
            shares: [],     // recommend hiding shares/links and inheriting server-side
            reminders: [],  // allow child-specific reminders if desired
            links: [],
            startUtcStr: "",
            endUtcStr: "",
            dueUtcStr: "",
        };
    }, [child, parent]);

    const [form, setForm] = useState<DiaryItemUpsertDtoEx>(initial);
    const [saving, setSaving] = useState(false);

    function isoToLocalInput(iso?: string | null) {
        if (!iso) return "";
        const d = new Date(iso);
        // datetime-local expects "YYYY-MM-DDTHH:mm"
        const pad = (n: number) => String(n).padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const min = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }

    const formatForDateInput = (dateString?: string | null): string => {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        // Convert to YYYY-MM-DD format
        return date.toISOString().split('T')[0];
    };

    const [dueUtc, setDueUtc] = useState<string>(isoToLocalInput(form.dueUtcStr));
    const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({ startDate: isoToLocalInput(form.startUtcStr) ?? '', endDate: isoToLocalInput(form.endUtcStr) ?? '' });
    function setDueDate(dueDate: string) {
        setDueUtc(dueDate);
        setField("dueUtc", dueDate)
    }

    function setEventDateRange(dateRange: { startDate?: string; endDate?: string }) {
        setDateRange(dateRange);
        setField("startUtc", dateRange.startDate);
        setField("endUtc", dateRange.endDate);
    }

    useEffect(() => {
        setDueUtc(isoToLocalInput(initial.dueUtcStr));
        setDateRange({ startDate: isoToLocalInput(initial.startUtcStr) ?? '', endDate: isoToLocalInput(initial.endUtcStr) ?? '' });
    }, [initial]);

    //Payment Details
    const [paymentDetails, setPaymentDetails] = useState<DiaryPaymentDetailsDto | null>(null);

    async function getPaymentDetailsByDiaryItem(diaryItemId: number) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getPaymentDetailsByDiaryItem');

            return await get<DiaryPaymentDetailsDto>(`payment-schedules/payment-details/by-diary-item/${diaryItemId}`, accessToken);
        } catch (e: any) {
            console.error("Get Payment Details Error", e);
            return null;
        }

    }

    function formatMoney(v: number) {
        return Number(v || 0).toFixed(2);
    }

    // When opening the dialog for different child records, reset form
    React.useEffect(() => {
        if (!open) return;
        setForm(initial);
    }, [open, initial]);

    React.useEffect(() => {
        let alive = true;

        async function load() {
            setPaymentDetails(null);

            if (!child?.id) return;

            const details = await getPaymentDetailsByDiaryItem(child.id);

            if (!alive) return;

            setPaymentDetails(details);
        }

        if (open) load();

        return () => { alive = false; };
    }, [open, child?.id]);

    function setField<K extends keyof DiaryItemUpsertDtoEx>(key: K, value: DiaryItemUpsertDtoEx[K]) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function validate(): string | null {
        if (!form.title?.trim()) return "Title is required.";

        if (form.type === DiaryItemType.Task) {
            if (!form.dueUtc) return "Due date is required for tasks.";
        } else {
            if (!form.startUtc) return "Start date is required for events.";
            if (form.endUtc && form.startUtc && new Date(form.endUtc) < new Date(form.startUtc))
                return "End must be after start.";
        }

        return null;
    }

    async function handleSave() {
        const err = validate();
        if (err) {
            alert(err); 
            return;
        }

        if (parent?.id) {
            setSaving(true);
            try {
                const dto: DiaryItemUpsertDtoEx = {
                    ...form,
                    recurrenceRule: null,
                    parentDiaryItemId: parent?.id,
                };

                if (isEdit && child) {
                    await updateDiaryItem(child.id, dto);
                } else {
                        await createChildDiaryItem(parent.id, dto);
                }

                await onSaved(parent.id);
                onClose();
            } finally {
                setSaving(false);
            }
        }
    }

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (saving) return;
                if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                onClose();
            }}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{isEdit ? (isViewOnly ? "View linked item" : "Edit linked item") : "Add linked item"}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Title"
                        value={form.title}
                        onChange={(e) => setField("title", e.target.value)}
                        fullWidth
                        disabled={isViewOnly}
                    />

                    <TextField
                        label="Description"
                        value={form.description ?? ""}
                        onChange={(e) => setField("description", e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={isViewOnly}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                            label="Type"
                            value={form.type}
                            onChange={(e) => setField("type", Number(e.target.value) as any)}
                            disabled={isViewOnly || isPaymentItem}
                        >
                            <MenuItem value={DiaryItemType.Event}>Event</MenuItem>
                            <MenuItem value={DiaryItemType.Task}>Task</MenuItem>
                        </Select>
                    </FormControl>

                    {form.type === DiaryItemType.Task ? (
                        //<TextField
                        //    label="Due (UTC)"
                        //    type="date"
                        //    value={dueUtc}
                        //    onChange={(e) => setField("dueUtc", e.target.value)}
                        //    fullWidth
                        //    InputLabelProps={{ shrink: true }}
                        //    disabled={isViewOnly}
                        ///>
                        <DueDateInput
                            value={dueUtc}
                                onChange={setDueDate}
                                showTime={true}
                                label="Task Deadline"
                            />
                    ) : (
                        //<>
                            //<TextField
                            //        label="Start (UTC)"
                            //        type="datetime-local"
                            //    value={startUtc}
                            //    onChange={(e) => setField("startUtc", e.target.value)}
                            //        fullWidth
                            //        InputLabelProps={{ shrink: true }}
                            //        disabled={isViewOnly}
                            ///>
                            //<TextField
                            //        label="End (UTC)"
                            //        type="datetime-local"
                            //    value={endUtc}
                            //    onChange={(e) => setField("endUtc", e.target.value)}
                            //        fullWidth
                            //        InputLabelProps={{ shrink: true }}
                            //        disabled={isViewOnly}
                                ///>

                            //</>
                            <DateTimeRangeInput
                                value={dateRange}
                                onChange={setEventDateRange}
                                startLabel="Start"
                                endLabel="End"
                                validateRange={true}
                            />
                    )}

                    <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                            label="Category"
                            value={form.category}
                            onChange={(e) => setField("category", Number(e.target.value) as any)}
                            disabled={isViewOnly || isPaymentItem}
                        >
                            {Object.entries(DiaryCategory)
                                .filter(([k]) => isNaN(Number(k)))
                                .map(([k, v]) => (
                                    <MenuItem key={k} value={v as any}>{k}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Visibility"
                        value={DiaryVisibility[parent.visibility] ?? String(parent.visibility)}
                        fullWidth
                        disabled={isViewOnly || isPaymentItem}
                        helperText="Visibility is inherited from the parent item."
                    />
                </Stack>
                {paymentDetails && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Payment details
                                </Typography>

                                <Chip
                                    size="small"
                                    label={`Milestone ${paymentDetails.sequenceNumber}`}
                                    variant="outlined"
                                />
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                                Schedule: {paymentDetails.scheduleTitle}
                            </Typography>

                            {paymentDetails.milestoneLabel && (
                                <Typography variant="body2" color="text.secondary">
                                    Label: {paymentDetails.milestoneLabel}
                                </Typography>
                            )}

                            <Divider sx={{ my: 1 }} />

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <Typography variant="body2">
                                    <strong>Amount:</strong> {formatMoney(paymentDetails.amount)} {paymentDetails.currencyCode}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Paid:</strong> {formatMoney(paymentDetails.amountPaid)} {paymentDetails.currencyCode}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Outstanding:</strong> {formatMoney(paymentDetails.amountOutstanding)} {paymentDetails.currencyCode}
                                </Typography>
                            </Stack>

                            {paymentDetails.paidUtc && (
                                <Typography variant="body2" color="text.secondary">
                                    Paid on: {new Date(paymentDetails.paidUtc).toLocaleString()}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{isViewOnly ? "Close" : "Cancel"}</Button>
                {!isViewOnly && (
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {isEdit ? "Save" : "Add"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
